import type { InventoryItemNameRow } from '@/lib/api'

/**
 * Matches `ClearanceDealsRail` listing: null/undefined stock means "not tracked here" — still sellable.
 * Only explicit ≤0 counts as out of stock for the cart UI.
 */
export function itemAllowsCart(inStock: number | null | undefined): boolean {
  return inStock == null || Number(inStock) > 0
}

export function maxStockCap(inStock: number | null | undefined): number | undefined {
  if (inStock == null) return undefined
  const n = Math.floor(Number(inStock))
  return n > 0 ? n : undefined
}

export function hasClearancePricing(item: InventoryItemNameRow): boolean {
  const p = item.discount_percentage
  return (
    p != null &&
    p >= 1 &&
    p <= 50 &&
    item.discounted_price != null &&
    item.retail_price != null
  )
}

/** Price the shopper pays: discounted when on clearance, else retail. */
export function effectiveUnitPrice(item: InventoryItemNameRow): number | null {
  if (hasClearancePricing(item)) return item.discounted_price!
  return item.retail_price ?? null
}

/** POS placeholder brands ("Unassigned", "Vendor 1") are noise for shoppers. */
function isPlaceholderBrand(brand: string): boolean {
  return /^(unassigned|vendor\s*\d*)$/i.test(brand.trim())
}

/** "Brand · Size" subtitle, skipping placeholder brands and empty parts. */
export function itemMetaLine(item: InventoryItemNameRow): string {
  const brand = item.brand_name && !isPlaceholderBrand(item.brand_name) ? item.brand_name : null
  return [brand, item.size_label].filter(Boolean).join(' · ')
}

const MAX_DEALS_POOL = 48

/**
 * "Clearance & deals" selection, shared by the home rail and `/items?deals=1`:
 * in-stock discounted rows (or, when none are discounted, all priced in-stock rows),
 * half picked by lowest price and half by lowest stock, capped at 48.
 */
export function pickClearanceDeals(items: InventoryItemNameRow[]): InventoryItemNameRow[] {
  const eligibleBase = items.filter(
    (r) => r.retail_price != null && (r.in_stock == null || Number(r.in_stock) > 0)
  )
  if (eligibleBase.length === 0) return []

  const discounted = eligibleBase.filter(hasClearancePricing)
  const eligible = discounted.length > 0 ? discounted : eligibleBase

  const half = Math.ceil(MAX_DEALS_POOL / 2)
  const byPrice = [...eligible].sort(
    (a, b) => (a.retail_price ?? 0) - (b.retail_price ?? 0)
  )
  const valuePicks = byPrice.slice(0, half)

  const withStock = eligible.filter((r) => r.in_stock != null && Number(r.in_stock) > 0)
  const byStock = [...withStock].sort(
    (a, b) => Number(a.in_stock ?? 0) - Number(b.in_stock ?? 0)
  )
  const stockPicks = byStock.slice(0, half)

  const seen = new Set<string>()
  const out: InventoryItemNameRow[] = []
  for (const r of [...valuePicks, ...stockPicks]) {
    if (seen.has(r.inventory_id)) continue
    seen.add(r.inventory_id)
    out.push(r)
    if (out.length >= MAX_DEALS_POOL) break
  }
  return out
}
